// src/pages/Profile.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Chip,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AccountCircle,
  Email,
  CalendarToday,
  Security,
  ArrowBack,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

import UserAvatar from '../components/common/UserAvatar';
import { updateMyProfile } from '../store/thunks/authThunks';
import { resizeImageToBase64, validateAvatarFile } from '../utils/avatar';

const ProfilePage = () => {
  const { user, isAuthenticated, access, accessChecked } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // ===== Редактирование имени =====
  const currentName = access?.display_name || user?.traits?.name || '';
  const [name, setName] = useState(currentName);
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState(null);
  const [nameSuccess, setNameSuccess] = useState(false);

  // ===== Редактирование аватара =====
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarError, setAvatarError] = useState(null);
  const [avatarSuccess, setAvatarSuccess] = useState(false);

  useEffect(() => {
    setName(currentName);
  }, [currentName]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated || !user) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  if (!accessChecked) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Загрузка профиля…
        </Typography>
      </Container>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Неверный формат даты';

      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Неверный формат даты';
    }
  };

  const userEmail = access?.email || user?.traits?.email || user?.email || 'Не указан';

  // ===== Сохранение имени =====
  const handleSaveName = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2 || trimmed.length > 64) {
      setNameError('Имя должно быть от 2 до 64 символов');
      return;
    }
    if (trimmed === currentName) {
      setNameError('Имя не изменилось');
      return;
    }

    setNameSaving(true);
    setNameError(null);
    setNameSuccess(false);

    try {
      await dispatch(updateMyProfile({ display_name: trimmed })).unwrap();
      setNameSuccess(true);
      setTimeout(() => setNameSuccess(false), 3000);
    } catch (err) {
      setNameError(err?.message || 'Не удалось сохранить имя');
    } finally {
      setNameSaving(false);
    }
  };

  // ===== Загрузка аватара =====
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const validation = validateAvatarFile(file);
    if (!validation.ok) {
      setAvatarError(validation.error);
      return;
    }

    setAvatarSaving(true);
    setAvatarError(null);
    setAvatarSuccess(false);

    try {
      const base64 = await resizeImageToBase64(file);
      await dispatch(updateMyProfile({ avatar_base64: base64 })).unwrap();
      setAvatarSuccess(true);
      setTimeout(() => setAvatarSuccess(false), 3000);
    } catch (err) {
      setAvatarError(err?.message || 'Не удалось загрузить аватар');
    } finally {
      setAvatarSaving(false);
    }
  };

  // ===== Удаление аватара =====
  const handleDeleteAvatar = async () => {
    setAvatarSaving(true);
    setAvatarError(null);
    setAvatarSuccess(false);

    try {
      await dispatch(updateMyProfile({ avatar_base64: null })).unwrap();
      setAvatarSuccess(true);
      setTimeout(() => setAvatarSuccess(false), 3000);
    } catch (err) {
      setAvatarError(err?.message || 'Не удалось удалить аватар');
    } finally {
      setAvatarSaving(false);
    }
  };

  const hasCustomAvatar = Boolean(access?.avatar_base64);

  return (
    <Container component="main" maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Заголовок и аватар */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4, flexWrap: 'wrap' }}>
          <Box sx={{ position: 'relative' }}>
            <UserAvatar
              access={access}
              size="hero"
              showBorder
              tooltip={currentName || userEmail}
            />

            <Tooltip title="Загрузить аватар">
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarSaving}
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                  width: 32,
                  height: 32,
                  boxShadow: 2,
                }}
                size="small"
              >
                <PhotoCameraIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            {hasCustomAvatar && (
              <Tooltip title="Удалить свой аватар">
                <IconButton
                  onClick={handleDeleteAvatar}
                  disabled={avatarSaving}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    bgcolor: 'error.main',
                    color: 'error.contrastText',
                    '&:hover': { bgcolor: 'error.dark' },
                    width: 28,
                    height: 28,
                    boxShadow: 2,
                  }}
                  size="small"
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={handleFileSelect}
            />

            {avatarSaving && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={32} sx={{ color: 'white' }} />
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography component="h1" variant="h4" gutterBottom>
              {currentName || 'Профиль пользователя'}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label="Активный" color="success" size="small" icon={<Security />} />
              {access?.system_role && (
                <Chip
                  label={access.system_role}
                  size="small"
                  variant="outlined"
                  color={
                    access.system_role === 'superadmin'
                      ? 'error'
                      : access.system_role === 'admin'
                      ? 'warning'
                      : access.system_role === 'curator'
                      ? 'info'
                      : 'default'
                  }
                />
              )}
              {!hasCustomAvatar && (
                <Chip label="DiceBear" size="small" variant="outlined" />
              )}
            </Stack>
          </Box>
        </Box>

        {avatarError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAvatarError(null)}>
            {avatarError}
          </Alert>
        )}
        {avatarSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAvatarSuccess(false)}>
            Аватар обновлён
          </Alert>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Редактирование имени */}
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Имя пользователя
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 3 }}>
          <TextField
            fullWidth
            label="Имя"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={nameSaving}
            error={Boolean(nameError)}
            helperText={nameError || 'От 2 до 64 символов'}
            inputProps={{ maxLength: 64 }}
          />
          <Button
            variant="contained"
            onClick={handleSaveName}
            disabled={nameSaving || name.trim() === currentName}
            startIcon={nameSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ mt: 1, whiteSpace: 'nowrap', minWidth: 140 }}
          >
            {nameSaving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </Box>

        {nameSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNameSuccess(false)}>
            Имя обновлено
          </Alert>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Информация о пользователе */}
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Основная информация
        </Typography>

        <List sx={{ mb: 3 }}>
          <ListItem>
            <ListItemIcon>
              <AccountCircle color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Идентификатор"
              secondary={access?.kratos_identity_id || user.id || 'Не указан'}
            />
          </ListItem>

          <Divider variant="inset" component="li" />

          <ListItem>
            <ListItemIcon>
              <Email color="primary" />
            </ListItemIcon>
            <ListItemText primary="Email адрес" secondary={userEmail} />
          </ListItem>

          <Divider variant="inset" component="li" />

          <ListItem>
            <ListItemIcon>
              <CalendarToday color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Дата создания"
              secondary={formatDate(access?.created_at || user.created_at)}
            />
          </ListItem>
        </List>

        {/* Кнопки действий */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
          >
            Назад
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
          >
            На главную
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ProfilePage;
